import React, { useState, useEffect } from 'react';
import { FaBirthdayCake, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { get } from '../../helpers/apiHelper';
import useAuthStore from '../../store/authStore';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

const BirthdayCalendar = () => {
    const { user } = useAuthStore();
    const navigate = useNavigate();
    const [clientes, setClientes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentMonth, setCurrentMonth] = useState(new Date()); // Current month being displayed
    const [expandedDay, setExpandedDay] = useState(null); // To show detailed birthdays for a day

    useEffect(() => {
        const fetchClientes = async () => {
            try {
                setLoading(true);
                const marcasResponse = await get('marcas');
                if (marcasResponse && marcasResponse.length > 0) {
                    const marcaId = marcasResponse[0]._id;
                    const response = await get(`marcas/${marcaId}/clientes`);
                    // Filter clients that have a valid fechaCumpleanos
                    const clientsWithBirthdays = response.filter(c => c.fechaCumpleanos && !isNaN(new Date(c.fechaCumpleanos).getTime()));
                    setClientes(clientsWithBirthdays);
                } else {
                    setError('No se encontraron marcas para este usuario.');
                }
            } catch (err) {
                console.error('Error fetching clients for birthday calendar:', err);
                setError('No se pudieron cargar los clientes para el calendario de cumpleaños.');
                Swal.fire('Error', 'No se pudieron cargar los clientes para el calendario de cumpleaños.', 'error');
            } finally {
                setLoading(false);
            }
        };
        fetchClientes();
    }, []);

    // Helper to get the start of the month
    const getStartOfMonth = (date) => {
        return new Date(date.getFullYear(), date.getMonth(), 1);
    };

    // Helper to get the end of the month
    const getEndOfMonth = (date) => {
        return new Date(date.getFullYear(), date.getMonth() + 1, 0);
    };

    // Get all dates for the current month view
    const getMonthDates = (date) => {
        const startOfMonth = getStartOfMonth(date);
        const endOfMonth = getEndOfMonth(date);

        // Get the first Monday of the month (or previous month if month starts on other day)
        const firstDay = startOfMonth.getDay();
        const daysToMonday = firstDay === 0 ? -6 : 1 - firstDay;
        const calendarStart = new Date(startOfMonth);
        calendarStart.setDate(startOfMonth.getDate() + daysToMonday);

        const dates = [];
        const currentDate = new Date(calendarStart);

        // Generate 42 days (6 weeks) to ensure we cover the entire month
        for (let i = 0; i < 42; i++) {
            dates.push(new Date(currentDate));
            currentDate.setDate(currentDate.getDate() + 1);
        }

        return dates;
    };

    const monthDates = getMonthDates(currentMonth);

    const getBirthdaysForDate = (date) => {
        return clientes.filter(cliente => {
            const birthday = new Date(cliente.fechaCumpleanos);
            return birthday.getMonth() === date.getMonth() && birthday.getDate() === date.getDate();
        });
    };

    const goToPreviousMonth = () => {
        const newMonth = new Date(currentMonth);
        newMonth.setMonth(newMonth.getMonth() - 1);
        setCurrentMonth(newMonth);
        setExpandedDay(null);
    };

    const goToNextMonth = () => {
        const newMonth = new Date(currentMonth);
        newMonth.setMonth(newMonth.getMonth() + 1);
        setCurrentMonth(newMonth);
        setExpandedDay(null);
    };

    const handleClientClick = (clienteId) => {
        navigate(`/statistics/customer/${clienteId}`);
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const monthNames = [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];

    if (loading) {
        return (
            <div className="bg-white rounded-lg shadow p-6 text-center">
                <p className="text-gray-600">Cargando calendario de cumpleaños...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white rounded-lg shadow p-6 text-center text-red-600">
                <p>{error}</p>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center justify-between">
                Calendario de Cumpleaños - {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                <div className="flex space-x-2">
                    <button
                        onClick={goToPreviousMonth}
                        className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600"
                        aria-label="Mes anterior"
                    >
                        <FaChevronLeft />
                    </button>
                    <button
                        onClick={goToNextMonth}
                        className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600"
                        aria-label="Mes siguiente"
                    >
                        <FaChevronRight />
                    </button>
                </div>
            </h2>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 text-center text-sm font-medium text-gray-500 mb-2">
                {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map(day => (
                    <div key={day} className="py-2">{day}</div>
                ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
                {monthDates.map((date, index) => {
                    const birthdays = getBirthdaysForDate(date);
                    const isToday = date.toDateString() === today.toDateString();
                    const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
                    const isExpanded = expandedDay && expandedDay.toDateString() === date.toDateString();

                    return (
                        <div
                            key={index}
                            className={`relative p-2 rounded-lg text-center cursor-pointer min-h-[80px] border
                                ${isToday ? 'bg-blue-100 border-blue-400' : 'bg-gray-50 hover:bg-gray-100 border-gray-200'}
                                ${!isCurrentMonth ? 'text-gray-400 bg-gray-25' : 'text-gray-800'}
                            `}
                            onClick={() => setExpandedDay(isExpanded ? null : date)}
                        >
                            <span className={`text-sm font-semibold ${isToday ? 'text-blue-700' : isCurrentMonth ? 'text-gray-800' : 'text-gray-400'}`}>
                                {date.getDate()}
                            </span>

                            {birthdays.length > 0 && (
                                <div className="mt-1 space-y-1">
                                    {birthdays.slice(0, 2).map((cliente, i) => (
                                        <div
                                            key={i}
                                            className="flex items-center justify-center space-x-1 text-xs"
                                        >
                                            <FaBirthdayCake className="text-pink-500" />
                                            <span
                                                className="text-pink-600 hover:text-pink-800 hover:underline cursor-pointer truncate"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleClientClick(cliente._id);
                                                }}
                                                title={`Ver estadísticas de ${cliente.nombre} ${cliente.apellido}`}
                                            >
                                                {cliente.nombre}
                                            </span>
                                        </div>
                                    ))}
                                    {birthdays.length > 2 && (
                                        <div className="text-xs text-pink-500">
                                            +{birthdays.length - 2} más
                                        </div>
                                    )}
                                </div>
                            )}

                            {isExpanded && (
                                <div className="absolute left-0 right-0 mt-2 p-3 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                                    <h4 className="font-semibold text-sm mb-2">
                                        Cumpleaños el {date.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })}:
                                    </h4>
                                    {birthdays.length > 0 ? (
                                        <ul className="text-xs text-left space-y-1">
                                            {birthdays.map(cliente => (
                                                <li
                                                    key={cliente._id}
                                                    className="flex items-center hover:bg-gray-50 p-1 rounded cursor-pointer"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleClientClick(cliente._id);
                                                    }}
                                                >
                                                    <FaBirthdayCake className="text-pink-500 mr-2" />
                                                    <span className="hover:text-pink-600 hover:underline">
                                                        {cliente.nombre} {cliente.apellido}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-xs text-gray-500">No hay cumpleaños este día.</p>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default BirthdayCalendar;